import { setting } from '@bz/request';
import { getDeviceId } from '@bz/socket';

import type { BasicInfo } from '../type';

const UNKNOWN = 'unknown';

const getEventId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getSafeNavigator = () => {
  if (typeof navigator === 'undefined') return null;
  return navigator;
};

const getBrowserInfo = (ua: string) => {
  const rules = [
    { name: 'Edge', pattern: /Edg\/([\d.]+)/i },
    { name: 'Opera', pattern: /OPR\/([\d.]+)/i },
    { name: 'Chrome', pattern: /Chrome\/([\d.]+)/i },
    { name: 'Safari', pattern: /Version\/([\d.]+).*Safari/i },
    { name: 'Firefox', pattern: /Firefox\/([\d.]+)/i },
    { name: 'IE', pattern: /(?:MSIE |rv:)([\d.]+)/i },
  ] as const;

  for (const rule of rules) {
    const matched = ua.match(rule.pattern);
    if (matched) {
      return {
        browser: rule.name,
        browser_version: matched[1] || UNKNOWN,
      };
    }
  }

  return {
    browser: UNKNOWN,
    browser_version: UNKNOWN,
  };
};

const getOsInfo = (ua: string) => {
  const rules = [
    {
      name: 'Windows',
      pattern: /Windows NT ([\d.]+)/i,
      formatter: (version: string) =>
        ({
          '10.0': '10/11',
          '6.3': '8.1',
          '6.2': '8',
          '6.1': '7',
        })[version] || version,
    },
    {
      name: 'Android',
      pattern: /Android ([\d.]+)/i,
      formatter: (version: string) => version,
    },
    {
      name: 'iOS',
      pattern: /(?:CPU (?:iPhone )?OS|iPhone OS) ([\d_]+)/i,
      formatter: (version: string) => version.replace(/_/g, '.'),
    },
    {
      name: 'macOS',
      pattern: /Mac OS X ([\d_]+)/i,
      formatter: (version: string) => version.replace(/_/g, '.'),
    },
    {
      name: 'Linux',
      pattern: /Linux/i,
      formatter: () => UNKNOWN,
    },
  ] as const;

  for (const rule of rules) {
    const matched = ua.match(rule.pattern);
    if (matched) {
      return {
        os: rule.name,
        os_version: rule.formatter(matched[1] || UNKNOWN),
      };
    }
  }

  return {
    os: UNKNOWN,
    os_version: UNKNOWN,
  };
};

const getDeviceModel = (ua: string) => {
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';

  const androidMatched = ua.match(/Android [\d.]+;\s*([^;)\]]+?)(?:\s+Build|;|\))/i);
  if (androidMatched?.[1]) {
    return androidMatched[1].trim();
  }

  if (/Linux/i.test(ua)) return 'Linux PC';
  return UNKNOWN;
};

const getDeviceType = (ua: string, maxTouchPoints: number) => {
  if (/Mobile|Android|iPhone|iPod|Windows Phone/i.test(ua)) return 'mobile' as const;
  if (/iPad|Tablet/i.test(ua)) return 'mobile' as const;
  if (maxTouchPoints > 1 && /Macintosh/i.test(ua)) return 'mobile' as const;
  return 'PC' as const;
};

export const getBasicInfo = (): BasicInfo => {
  if (typeof window === 'undefined') {
    return {
      type: 'track',
      event_id: '',
      deviceId: '',
      distinct_id: '',
      anonymous_id: '',
      time: 0,
      timezone_offset: 0,
      user_time: '',
      url: '',
      url_host: '',
      url_path: '',
      title: '',
      user_agent: '',
      theme: '',
      lang: '',
      browser_lang: '',
      os_lang: '',
      platform: 'web',
      device_type: 'PC',
      device_model: '',
      browser: '',
      browser_version: '',
      os: '',
      os_version: '',
    };
  }

  const nav = getSafeNavigator();
  const ua = nav?.userAgent || '';

  const deviceId = getDeviceId() || '';
  const distinct_id = account.isLogin ? String(account.userId) : deviceId;
  const anonymous_id = deviceId;

  const timezone_offset = new Date().getTimezoneOffset();
  const user_time = new Date().toISOString();
  const { href, host, pathname } = window.location;
  const browserInfo = getBrowserInfo(ua);
  const osInfo = getOsInfo(ua);
  const device_type = getDeviceType(ua, nav?.maxTouchPoints || 0);
  const browser_lang = nav?.language || '';
  const os_lang = Intl.DateTimeFormat().resolvedOptions().locale || browser_lang;

  return {
    type: 'track',
    event_id: getEventId(),
    deviceId,
    distinct_id,
    anonymous_id,
    time: new Date().getTime(),
    timezone_offset,
    user_time,
    url: href,
    url_host: host,
    url_path: pathname,
    title: document.title || '',
    user_agent: ua,
    theme: String(setting.theme ?? ''),
    lang: setting.lang || browser_lang,
    browser_lang,
    os_lang,
    platform: device_type === 'mobile' ? 'h5' : 'web',
    device_type,
    device_model: getDeviceModel(ua),
    browser: browserInfo.browser,
    browser_version: browserInfo.browser_version,
    os: osInfo.os,
    os_version: osInfo.os_version,
  };
};
